import React, { useEffect, useRef, useState } from "react";
import ApexCharts from "apexcharts";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import { toast } from "react-toastify";
import { setMiniSidebar } from "../../../core/data/redux/commonSlice";
import CollapseHeader from "../../../core/common/collapse-header";
import { apiService } from "../../../services/api";
import {
  DashboardFaturamentoResponse,
  DashboardDisputasResponse,
  DashboardContratosAtivosResponse,
  DashboardFluxoCaixaResponse,
  DashboardOportunidadesPorEtapaResponse,
  DashboardOportunidadesPorPerfilResponse,
  DashboardPeriodo,
  DashboardQuadranteDetalhado,
  DashboardDetalhadoParams
} from "../../../types/dashboard";
import DashboardPeriodFilter, { DashboardFilterValue } from "./DashboardPeriodFilter";
import { getPeriodRange, toISODate, formatPeriodLabel } from "./periodUtils";
import { RelatorioAutorizacoesFaturamentoDocument } from "./RelatorioAutorizacoesFaturamentoDocument";
import { RelatorioIndicadoresDetalhadoDocument } from "./RelatorioIndicadoresDetalhadoDocument";
import "./dashboardAnalytics.css";

const makeDefaultFilter = (periodo: DashboardFilterValue["periodo"]): DashboardFilterValue => ({
  periodo,
  referenceDate: new Date(),
  customStart: null,
  customEnd: null,
});


const getBarColumnWidth = (categoriesCount: number): string => {
  if (categoriesCount <= 1) return '20%';
  if (categoriesCount <= 2) return '30%';
  if (categoriesCount <= 4) return '45%';
  return '55%';
};


const renderChartWhenReady = (
  container: HTMLElement,
  chart: any,
  getIsCancelled: () => boolean,
  onRendered: () => void
): (() => void) => {
  if (getIsCancelled()) return () => {};

  if (container.offsetWidth > 0) {
    try { chart.render(); onRendered(); } catch (e) { console.warn('Erro ao renderizar gráfico:', e); }
    return () => {};
  }

  const observer = new ResizeObserver((entries) => {
    if (getIsCancelled()) {
      observer.disconnect();
      return;
    }
    const width = entries[0]?.contentRect?.width ?? container.offsetWidth;
    if (width > 0) {
      observer.disconnect();
      try { chart.render(); onRendered(); } catch (e) { console.warn('Erro ao renderizar gráfico:', e); }
    }
  });
  observer.observe(container);
  return () => observer.disconnect();
};

const debugStatusPaneRef = (node: HTMLDivElement | null) => {
  if (node) {
    console.log('%c[DEBUG REF STATUS] ATTACH', 'color:green;font-weight:bold', node, 'pai:', node.parentElement);
  } else {
    console.log('%c[DEBUG REF STATUS] DETACH (null)', 'color:red;font-weight:bold');
  }
};

type StatColor = 'blue' | 'green' | 'amber' | 'red' | 'slate';

const StatCard = ({
  icon,
  label,
  value,
  sub,
  color = 'blue',
  to
}: {
  icon: string;
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  color?: StatColor;
  // Quando informado, o card vira um link para a lista (ex.: contratos filtrados).
  to?: string;
}) => {
  const inner = (
    <>
      <div className={`stat-card__icon stat-card__icon--${color}`}>
        <i className={`ti ${icon}`} />
      </div>
      <div className="stat-card__label">{label}</div>
      <div className="stat-card__value">{value}</div>
      {sub && <div className="stat-card__sub">{sub}</div>}
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        target="_blank"
        rel="noopener noreferrer"
        className={`stat-card stat-card--${color} stat-card--link`}
      >
        {inner}
      </Link>
    );
  }

  return <div className={`stat-card stat-card--${color}`}>{inner}</div>;
};

const DashboardAnalytics = () => {
  // Separate Loading States
  const [loadingFaturamento, setLoadingFaturamento] = useState(true);
  const [loadingDisputas, setLoadingDisputas] = useState(true);
  const [loadingContratos, setLoadingContratos] = useState(true);
  const [loadingFluxoCaixa, setLoadingFluxoCaixa] = useState(true);
  const [exportandoFaturamentoPDF, setExportandoFaturamentoPDF] = useState(false);
  // Quadrante cujo relatório PDF está sendo gerado (null = nenhum)
  const [exportandoQuadrante, setExportandoQuadrante] = useState<DashboardQuadranteDetalhado | null>(null);

  // State for Data
  const [faturamentoData, setFaturamentoData] = useState<DashboardFaturamentoResponse | null>(null);
  const [disputasData, setDisputasData] = useState<DashboardDisputasResponse | null>(null);
  const [contratosData, setContratosData] = useState<DashboardContratosAtivosResponse | null>(null);
  const [fluxoCaixaData, setFluxoCaixaData] = useState<DashboardFluxoCaixaResponse | null>(null);

  // State for Filters
  const [faturamentoFiltro, setFaturamentoFiltro] = useState<DashboardFilterValue>(() => makeDefaultFilter('mensal'));
  const [disputasFiltro, setDisputasFiltro] = useState<DashboardFilterValue>(() => makeDefaultFilter('semanal'));
  const [contratosFiltro, setContratosFiltro] = useState<DashboardFilterValue>(() => makeDefaultFilter('mensal'));
  const [fluxoCaixaFiltro, setFluxoCaixaFiltro] = useState<DashboardFilterValue>(() => makeDefaultFilter('mensal'));

  // Operacional: abas Status / Etapa do Funil / Perfil
  const [operacionalTab, setOperacionalTab] = useState<'status' | 'etapa' | 'perfil'>('status');

  const [etapaFiltro, setEtapaFiltro] = useState<DashboardFilterValue>(() => makeDefaultFilter('mensal'));
  const [etapaData, setEtapaData] = useState<DashboardOportunidadesPorEtapaResponse | null>(null);
  const [loadingEtapa, setLoadingEtapa] = useState(false);
  // Guarda a última chave (periodo:inicio:fim) já buscada, pra não refazer a
  // mesma requisição ao trocar de aba e voltar sem mudar o período.
  const etapaFetchedKeyRef = useRef<string | null>(null);

  const [perfilFiltro, setPerfilFiltro] = useState<DashboardFilterValue>(() => makeDefaultFilter('mensal'));
  const [perfilData, setPerfilData] = useState<DashboardOportunidadesPorPerfilResponse | null>(null);
  const [loadingPerfil, setLoadingPerfil] = useState(false);
  const perfilFetchedKeyRef = useRef<string | null>(null);
  const [pessoaSelecionadaId, setPessoaSelecionadaId] = useState<number | null>(null);

  // Refs for Charts
  const faturamentoChartRef = useRef(null);
  const disputasChartRef = useRef(null);
  const etapaChartRef = useRef(null);
  const perfilChartRef = useRef(null);

  // Colapsa o menu lateral ao entrar no dashboard, restaura ao sair
  const dispatch = useDispatch();
  const currentMiniSidebar = useSelector((state: any) => state.miniSidebar);
  const prevMiniSidebarRef = useRef(currentMiniSidebar);

  useEffect(() => {
    dispatch(setMiniSidebar(true));
    return () => {
      dispatch(setMiniSidebar(prevMiniSidebarRef.current));
    };
  }, [dispatch]);

  // DIAGNÓSTICO TEMPORÁRIO - remover depois de descobrir a causa do bug da
  // aba Status sumindo do DOM ao vir de /crm/oportunidades. Intercepta
  // remoções de nó pra pegar o stack trace de quem está removendo a div.
  useEffect(() => {
    const origRemoveChild = Node.prototype.removeChild;
    const origRemove = Element.prototype.remove;
    const isSuspect = (node: any) =>
      node && (node.id === 'disputas-chart' || (node.classList && node.classList.contains && node.classList.contains('tab-pane')));

    Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
      if (isSuspect(child)) {
        console.trace('🕵️ removeChild em', child, 'pai:', this);
      }
      return origRemoveChild.call(this, child) as T;
    };
    Element.prototype.remove = function (this: Element) {
      if (isSuspect(this)) {
        console.trace('🕵️ .remove() em', this);
      }
      return origRemove.call(this);
    };

    return () => {
      Node.prototype.removeChild = origRemoveChild;
      Element.prototype.remove = origRemove;
    };
  }, []);

  // DIAGNÓSTICO TEMPORÁRIO 2: o render (função do componente) está sendo
  // chamado com sucesso e dado válido, sem lançar erro, mas a tela continua
  // em branco - ou seja, o commit do React não está batendo com o que a
  // gente vê na tela. Duas hipóteses: (a) alguma mutação de DOM que não passa
  // por removeChild/.remove() (ex.: innerHTML='' num ancestral, que o trap
  // acima não pega), ou (b) essa instância do componente está "órfã"
  // (desmontada mas ainda rodando efeitos/fetches por uma promise pendente)
  // e o que a gente vê logado no console não é a árvore realmente visível.
  // Esse efeito: (1) marca essa instância com um ID único pra rastrear nos
  // logs, (2) usa MutationObserver (pega QUALQUER mutação, inclusive
  // innerHTML) pra logar toda vez que o número de .tab-pane dentro de
  // .tab-content mudar, e (3) confirma se essa instância ainda está anexada
  // ao documento sempre que o fetch de disputas terminar.
  const instanceIdRef = useRef(Math.random().toString(36).slice(2, 8));
  useEffect(() => {
    console.log(`%c[DEBUG MOUNT] instância ${instanceIdRef.current} montou`, 'color:blue;font-weight:bold');
    return () => {
      console.log(`%c[DEBUG MOUNT] instância ${instanceIdRef.current} desmontou`, 'color:blue;font-weight:bold');
    };
  }, []);

  useEffect(() => {
    const tabContent = document.querySelector('.tab-content');
    if (!tabContent) {
      console.warn(`%c[DEBUG MUTATION] instância ${instanceIdRef.current}: .tab-content não encontrado no mount do observer`, 'color:purple');
      return;
    }
    const logCount = (label: string) => {
      const panes = tabContent.querySelectorAll(':scope > .tab-pane');
      console.log(`%c[DEBUG MUTATION] (${label}) instância ${instanceIdRef.current}: ${panes.length} tab-panes`, 'color:purple;font-weight:bold', Array.from(panes).map(p => p.className));
    };
    logCount('inicial');
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.removedNodes.length > 0 || m.addedNodes.length > 0) {
          console.trace(`%c[DEBUG MUTATION] instância ${instanceIdRef.current}: mutação em .tab-content`, 'color:purple;font-weight:bold', 'removidos:', m.removedNodes, 'adicionados:', m.addedNodes);
          logCount('após mutação');
        }
      }
    });
    observer.observe(tabContent, { childList: true });
    return () => observer.disconnect();
  }, []);

  // --- MOCK DATA GENERATORS (Fallback) ---
  const getMockFaturamento = (periodo: DashboardPeriodo): DashboardFaturamentoResponse => {
    const isYearly = periodo === 'anual';
    const count = isYearly ? 12 : (periodo === 'mensal' ? 30 : 7);
    const historico = Array.from({ length: count }, (_, i) => ({
      data: isYearly ? `Mês ${i+1}` : `Dia ${i+1}`,
      valor_real: Math.floor(Math.random() * 50000) + 20000,
      valor_objetivo: 50000
    }));

    const faturamentoTotal = historico.reduce((acc, curr) => acc + curr.valor_real, 0);

    return {
      success: true,
      data: {
        periodo: periodo,
        data_referencia: new Date().toISOString().split('T')[0],
        faturamento: {
          valor_real: faturamentoTotal,
          valor_objetivo: historico.reduce((acc, curr) => acc + curr.valor_objetivo, 0),
          percentual_alcance: 85,
          variacao_percentual: 5,
          status_indicador: 'success'
        },
        detalhes: {
          faturamento_total: faturamentoTotal,
          total_receitas: count,
          receita_media: Math.round(faturamentoTotal / count)
        },
        comparacao_periodo_anterior: {
          valor_anterior: Math.round(faturamentoTotal * 0.85),
          variacao: 12.5,
          periodo_anterior: '11/2025'
        },
        historico: historico
      }
    };
  };

  const getMockDisputas = (periodo: DashboardPeriodo): DashboardDisputasResponse => {
    return {
      success: true,
      data: {
        novos_oportunidades: { quantidade_real: 12, quantidade_objetivo: 15, status_indicador: 'warning', valor_real: 480000 },
        oportunidades_perdidas: { quantidade_real: 2, quantidade_objetivo: 3, status_indicador: 'success', valor_real: 95000 },
        oportunidades_suspensas: { quantidade_real: 3, quantidade_objetivo: 5, status_indicador: 'warning', valor_real: 210000 },
        oportunidades_monitoramento: { quantidade_real: 4, quantidade_objetivo: 6, status_indicador: 'warning', valor_real: 340000 },
        oportunidades_andamento: { quantidade_real: 25, quantidade_objetivo: 30, status_indicador: 'success', valor_real: 1250000 },
        oportunidades_canceladas: { quantidade_real: 1, quantidade_objetivo: 3, status_indicador: 'success', valor_real: 45000 },
        disputas: { quantidade_real: 5, quantidade_objetivo: 10, status_indicador: 'warning', valor_real: 620000 },
        vitorias: { quantidade_real: 8, quantidade_objetivo: 12, status_indicador: 'warning', valor_real: 890000 },
        historico: [] // Simplified for now
      }
    };
  };

  const getMockContratos = (periodo: DashboardPeriodo): DashboardContratosAtivosResponse => ({
    success: true,
    data: {
      periodo: periodo,
      contrato_ativo_total: { quantidade_contratos: 142, valor_real: 0, valor_objetivo: 0, percentual_alcance: 0, status_indicador: 'neutral' },
      contratos_a_vencer: { quantidade_contratos: 15, valor_real: 0, valor_objetivo: 0, percentual_alcance: 0, status_indicador: 'danger' },
      novos_contratos: { quantidade_contratos: 8, valor_real: 0, valor_objetivo: 0, percentual_alcance: 0, status_indicador: 'success' },
      renovacoes_contrato: { quantidade_renovacoes: 5, valor_real: 0, valor_objetivo: 0, percentual_alcance: 0, status_indicador: 'info' }
    }
  });

  const getMockFluxoCaixa = (): DashboardFluxoCaixaResponse => ({
    success: true,
    data: {
      data_base: new Date().toISOString().split('T')[0],
      periodo_analise: 'mensal',
      saldo_contas_correntes: { valor_real: 125000.50, valor_objetivo: 500000, percentual_alcance: 25, status_indicador: 'neutral', quantidade_contas: 3, sem_lancamento_periodo: false },
      contas_a_pagar: { valor_real: 45000.00, quantidade_titulos: 18, vencidos: 2, maior_vencimento: '2025-12-30' },
      contas_a_receber: { valor_real: 68000.00, quantidade_titulos: 25, vencidos: 3, proximo_recebimento: '2025-12-26' },
      historico_por_periodo: Array.from({ length: 6 }, (_, i) => ({
        periodo: `2025-${String(12 - i).padStart(2, '0')}`,
        valor_entradas: Math.floor(Math.random() * 80000) + 20000,
        valor_saidas: Math.floor(Math.random() * 60000) + 10000,
        qtd_entradas: Math.floor(Math.random() * 20) + 10,
        qtd_saidas: Math.floor(Math.random() * 15) + 8,
        saldo: Math.floor(Math.random() * 40000) + 10000
      })),
      fluxo_previsto: {
        saldo_atual: 125000.50,
        entradas_30_dias: 160000,
        saidas_30_dias: 110000,
        saldo_projetado_30_dias: 175000.50,
        variacao_percentual: 40
      },
      // Compatibilidade com legado
      historico: Array.from({ length: 6 }, (_, i) => ({
        data: `Mês ${i+1}`,
        receitas: Math.floor(Math.random() * 80000) + 20000,
        despesas: Math.floor(Math.random() * 60000) + 10000
      }))
    }
  });

  // --- INDEPENDENT FETCH FUNCTIONS ---

  // Resolve os parâmetros de data a partir de um filtro: usa o intervalo
  // customizado quando "personalizado", ou calcula o intervalo da
  // granularidade selecionada a partir da data de referência.
  const resolveDateRange = (filtro: DashboardFilterValue): { data_inicio: string; data_fim: string } => {
    if (filtro.periodo === 'personalizado' && filtro.customStart && filtro.customEnd) {
      return { data_inicio: filtro.customStart, data_fim: filtro.customEnd };
    }
    const { dataInicio, dataFim } = getPeriodRange(filtro.periodo, filtro.referenceDate);
    return { data_inicio: dataInicio, data_fim: dataFim };
  };

  const fetchFaturamento = async () => {
    setLoadingFaturamento(true);
    try {
      let fatData;
      try {
        const { data_inicio, data_fim } = resolveDateRange(faturamentoFiltro);
        fatData = await apiService.getDashboardFaturamento({
          periodo: faturamentoFiltro.periodo,
          data_inicio,
          data_fim,
          comparar_anterior: true
        });
        console.log("🟢 API Faturamento Response:", fatData);
      } catch (e) {
        console.warn("🔴 API Faturamento Failed:", e);
        fatData = getMockFaturamento(faturamentoFiltro.periodo);
      }
      setFaturamentoData(fatData);
    } catch (error) {
      console.error("Error fetching faturamento:", error);
    } finally {
      setLoadingFaturamento(false);
    }
  };

  const formatDataBR = (isoDate: string): string => {
    const [ano, mes, dia] = isoDate.split('-');
    return `${dia}/${mes}/${ano}`;
  };

  const handleExportarFaturamentoPDF = async () => {
    setExportandoFaturamentoPDF(true);
    try {
      const { data_inicio, data_fim } = resolveDateRange(faturamentoFiltro);

      const detalhado = await apiService.getDashboardFaturamentoDetalhado({
        periodo: faturamentoFiltro.periodo,
        data_inicio,
        data_fim
      });

      const itens = detalhado?.data?.itens || [];
      if (itens.length === 0) {
        toast.info("Nenhuma autorização com NF de venda emitida neste período.");
        return;
      }

      const periodoLabel = faturamentoFiltro.periodo === 'personalizado'
        ? `${formatDataBR(data_inicio)} a ${formatDataBR(data_fim)}`
        : formatPeriodLabel(faturamentoFiltro.periodo, faturamentoFiltro.referenceDate);

      const blob = await pdf(
        <RelatorioAutorizacoesFaturamentoDocument
          itens={itens}
          resumo={detalhado.data.resumo}
          periodoLabel={periodoLabel}
        />
      ).toBlob();

      saveAs(blob, `Relatorio_Autorizacoes_Faturamento_${data_inicio}_a_${data_fim}.pdf`);
      toast.success("Relatório PDF gerado com sucesso!");
    } catch (error) {
      console.error("Erro ao exportar PDF de faturamento:", error);
      toast.error("Falha ao gerar o PDF do relatório de faturamento.");
    } finally {
      setExportandoFaturamentoPDF(false);
    }
  };

  // Exporta o PDF de um quadrante (Status, Etapa, Perfil, Contratos, Fluxo de
  // Caixa) com uma tabela por card — mesmo comportamento do PDF de faturamento.
  const handleExportarQuadrantePDF = async (
    quadrante: DashboardQuadranteDetalhado,
    filtro: DashboardFilterValue,
    nomeArquivo: string
  ) => {
    setExportandoQuadrante(quadrante);
    try {
      const { data_inicio, data_fim } = resolveDateRange(filtro);

      // Contratos e Fluxo de Caixa buscam os cards com data_base (ou intervalo no
      // "personalizado"); o relatório precisa usar exatamente a mesma chamada
      // para os totais baterem com os cards.
      let params: DashboardDetalhadoParams;
      if (quadrante === 'contratos' || quadrante === 'fluxo-caixa') {
        params = filtro.periodo === 'personalizado' && filtro.customStart && filtro.customEnd
          ? { quadrante, periodo: 'personalizado', data_inicio: filtro.customStart, data_fim: filtro.customEnd }
          : { quadrante, periodo: filtro.periodo, data_base: toISODate(filtro.referenceDate) };
      } else {
        params = { quadrante, periodo: filtro.periodo, data_inicio, data_fim };
      }

      const detalhado = await apiService.getDashboardIndicadoresDetalhado(params);

      const secoes = detalhado?.data?.secoes || [];
      if (secoes.every((secao) => secao.itens.length === 0)) {
        toast.info("Nenhum registro encontrado neste período.");
        return;
      }

      const periodoLabel = filtro.periodo === 'personalizado'
        ? `${formatDataBR(data_inicio)} a ${formatDataBR(data_fim)}`
        : formatPeriodLabel(filtro.periodo, filtro.referenceDate);

      const blob = await pdf(
        <RelatorioIndicadoresDetalhadoDocument
          titulo={detalhado.data.titulo}
          periodoLabel={periodoLabel}
          observacao={detalhado.data.observacao}
          secoes={secoes}
        />
      ).toBlob();

      saveAs(blob, `${nomeArquivo}_${detalhado.data.data_inicio}_a_${detalhado.data.data_fim}.pdf`);
      toast.success("Relatório PDF gerado com sucesso!");
    } catch (error) {
      console.error(`Erro ao exportar PDF do quadrante ${quadrante}:`, error);
      toast.error("Falha ao gerar o PDF do relatório.");
    } finally {
      setExportandoQuadrante(null);
    }
  };

  const renderBotaoExportarQuadrante = (
    quadrante: DashboardQuadranteDetalhado,
    filtro: DashboardFilterValue,
    nomeArquivo: string,
    desabilitado: boolean
  ) => (
    <button
      type="button"
      className="btn btn-outline-danger btn-sm"
      onClick={() => handleExportarQuadrantePDF(quadrante, filtro, nomeArquivo)}
      disabled={exportandoQuadrante !== null || desabilitado}
    >
      {exportandoQuadrante === quadrante ? (
        <>
          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
          Gerando PDF...
        </>
      ) : (
        <>
          <i className="ti ti-file-type-pdf me-2"></i>
          Exportar PDF
        </>
      )}
    </button>
  );

  const fetchDisputas = async () => {
    setLoadingDisputas(true);
    try {
      let dispData;
      try {
        const { data_inicio, data_fim } = resolveDateRange(disputasFiltro);
        dispData = await apiService.getDashboardDisputasContratos({
          periodo: disputasFiltro.periodo,
          data_inicio,
          data_fim
        });
        console.log("🟢 API Disputas Response:", dispData);
      } catch (e) {
        console.warn("🔴 API Disputas Failed:", e);
        dispData = getMockDisputas(disputasFiltro.periodo);
      }
      // Garante campos novos (Monitoramento/Vitórias/Canceladas) mesmo se a API ainda não os retornar
      const emptyKPI = { quantidade_real: 0, quantidade_objetivo: 0, status_indicador: 'neutral' as const, valor_real: 0 };
      if (dispData?.data) {
        dispData.data.oportunidades_monitoramento = dispData.data.oportunidades_monitoramento ?? emptyKPI;
        dispData.data.vitorias = dispData.data.vitorias ?? emptyKPI;
        dispData.data.oportunidades_canceladas = dispData.data.oportunidades_canceladas ?? emptyKPI;
      }
      setDisputasData(dispData);
    } catch (error) {
      console.error("Error fetching disputas:", error);
    } finally {
      setLoadingDisputas(false);
    }
  };

  const fetchContratos = async () => {
    setLoadingContratos(true);
    try {
      let contData;
      try {
        if (contratosFiltro.periodo === 'personalizado' && contratosFiltro.customStart && contratosFiltro.customEnd) {
          contData = await apiService.getDashboardContratosAtivos({
            periodo: 'personalizado',
            data_inicio: contratosFiltro.customStart,
            data_fim: contratosFiltro.customEnd
          });
        } else {
          contData = await apiService.getDashboardContratosAtivos({
            periodo: contratosFiltro.periodo,
            data_base: toISODate(contratosFiltro.referenceDate)
          });
        }
        console.log("🟢 API Contratos Response:", contData);
      } catch (e) {
        console.warn("🔴 API Contratos Failed:", e);
        contData = getMockContratos(contratosFiltro.periodo);
      }
      setContratosData(contData);
    } catch (error) {
      console.error("Error fetching contratos:", error);
    } finally {
      setLoadingContratos(false);
    }
  };

  const fetchFluxoCaixa = async () => {
    setLoadingFluxoCaixa(true);
    try {
      let fluxData;
      try {
        if (fluxoCaixaFiltro.periodo === 'personalizado' && fluxoCaixaFiltro.customStart && fluxoCaixaFiltro.customEnd) {
          fluxData = await apiService.getDashboardFluxoCaixa({
            periodo: 'personalizado',
            data_inicio: fluxoCaixaFiltro.customStart,
            data_fim: fluxoCaixaFiltro.customEnd
          });
        } else {
          fluxData = await apiService.getDashboardFluxoCaixa({
            periodo: fluxoCaixaFiltro.periodo,
            data_base: toISODate(fluxoCaixaFiltro.referenceDate)
          });
        }
        console.log("🟢 API Fluxo Caixa Response:", fluxData);
      } catch (e) {
        console.warn("🔴 API Fluxo Failed:", e);
        fluxData = getMockFluxoCaixa();
      }
      setFluxoCaixaData(fluxData);
    } catch (error) {
      console.error("Error fetching fluxo caixa:", error);
    } finally {
      setLoadingFluxoCaixa(false);
    }
  };

  // --- EFFECTS ---

  useEffect(() => {
    fetchFaturamento();
  }, [faturamentoFiltro]);

  useEffect(() => {
    fetchDisputas();
  }, [disputasFiltro]);

  useEffect(() => {
    fetchContratos();
  }, [contratosFiltro]);

  useEffect(() => {
    fetchFluxoCaixa();
  }, [fluxoCaixaFiltro]);

  // Aba Etapa do Funil: busca só quando a aba está ativa, e só se o período
  // (chave periodo:inicio:fim) mudou desde a última busca - evita refazer a
  // mesma requisição ao trocar de aba e voltar.
  useEffect(() => {
    if (operacionalTab !== 'etapa') return;
    const { data_inicio, data_fim } = resolveDateRange(etapaFiltro);
    const cacheKey = `${etapaFiltro.periodo}:${data_inicio}:${data_fim}`;
    if (etapaFetchedKeyRef.current === cacheKey) return;
    etapaFetchedKeyRef.current = cacheKey;
    setLoadingEtapa(true);
    apiService.getDashboardOportunidadesPorEtapa({ periodo: etapaFiltro.periodo, data_inicio, data_fim })
      .then((res) => {
        // Garante campos novos (Cotação Amanda/Mayara) mesmo se a API ainda
        // não tiver sido atualizada em produção - evita crash por undefined.
        const emptyKPI = { quantidade_real: 0, quantidade_objetivo: 0, status_indicador: 'neutral' as const, valor_real: 0 };
        if (res?.data) {
          res.data.cotacao_amanda = res.data.cotacao_amanda ?? emptyKPI;
          res.data.cotacao_mayara = res.data.cotacao_mayara ?? emptyKPI;
        }
        setEtapaData(res);
      })
      .catch((e) => console.warn("🔴 API Etapa Funil Failed:", e))
      .finally(() => setLoadingEtapa(false));
  }, [operacionalTab, etapaFiltro]);

  // Aba Perfil: mesmo padrão de fetch lazy com cache local por período.
  useEffect(() => {
    if (operacionalTab !== 'perfil') return;
    const { data_inicio, data_fim } = resolveDateRange(perfilFiltro);
    const cacheKey = `${perfilFiltro.periodo}:${data_inicio}:${data_fim}`;
    if (perfilFetchedKeyRef.current === cacheKey) return;
    perfilFetchedKeyRef.current = cacheKey;
    setLoadingPerfil(true);
    apiService.getDashboardOportunidadesPorPerfil({ periodo: perfilFiltro.periodo, data_inicio, data_fim })
      .then((res) => {
        // Defesa contra API antiga/desatualizada: garante que `etapas` e
        // `por_etapa` de cada pessoa nunca sejam undefined (formato é dinâmico
        // agora - qualquer etapa ativa da conta, sem lista fixa).
        if (res?.data) {
          res.data.etapas = res.data.etapas ?? [];
          res.data.pessoas = (res.data.pessoas ?? []).map((p) => ({
            ...p,
            por_etapa: p.por_etapa ?? {}
          }));
        }
        setPerfilData(res);
        setPessoaSelecionadaId(res?.data?.pessoas?.[0]?.id_usuario ?? null);
      })
      .catch((e) => console.warn("🔴 API Perfil Failed:", e))
      .finally(() => setLoadingPerfil(false));
  }, [operacionalTab, perfilFiltro]);

  // --- CHART RENDERING ---

  // 1. Faturamento
  useEffect(() => {
    if (faturamentoChartRef.current && faturamentoData?.data?.historico) {
        if (faturamentoChartRef.current) {
             (faturamentoChartRef.current as HTMLElement).innerHTML = '';
        }

        const hist = faturamentoData.data.historico;
        const options = {
            series: [
              { name: 'Real', data: hist.map(h => h.valor_real) },
              { name: 'Objetivo', data: hist.map(h => h.valor_objetivo) }
            ],
            chart: { type: 'area', height: 350, toolbar: { show: false } },
            dataLabels: { enabled: false },
            stroke: { curve: 'smooth' },
            xaxis: { categories: hist.map(h => h.data) },
            colors: ['#2389D8', '#94A3B8'],
            tooltip: { y: { formatter: (val: number) => `R$ ${val.toLocaleString('pt-BR')}` } }
        };
        const chart = new ApexCharts(faturamentoChartRef.current, options);
        // Adia o render até o container ter largura real: se o container
        // ainda estiver com largura 0 (ex.: logo após a sidebar colapsar no
        // mount), o ApexCharts calcula NaN no SVG e quebra o render.
        let destruido = false;
        let renderizado = false;
        const stopObserving = renderChartWhenReady(faturamentoChartRef.current, chart, () => destruido, () => { renderizado = true; });
        return () => {
            destruido = true;
            stopObserving();
            // Só destrói se o render() realmente rodou - destruir um gráfico
            // que nunca chegou a desenhar não tem o que limpar e pode mexer
            // no DOM de forma inesperada.
            if (renderizado) {
                try { chart.destroy(); } catch (e) { console.warn('Erro ao destruir gráfico:', e); }
            }
        };
    }
  }, [faturamentoData]);

  // 2. Disputas
  useEffect(() => {
    if (disputasChartRef.current && disputasData?.data) {
        if (disputasChartRef.current) {
             (disputasChartRef.current as HTMLElement).innerHTML = '';
        }

        const d = disputasData.data;
        const categories = ['Em Andamento', 'Suspenso', 'Cancelada', 'Ganha', 'Perdida'];
        const realData = [
          d.oportunidades_andamento.quantidade_real,
          d.oportunidades_suspensas.quantidade_real,
          d.oportunidades_canceladas.quantidade_real,
          d.vitorias.quantidade_real,
          d.oportunidades_perdidas.quantidade_real
        ];
        const goalData = [
          d.oportunidades_andamento.quantidade_objetivo,
          d.oportunidades_suspensas.quantidade_objetivo,
          d.oportunidades_canceladas.quantidade_objetivo,
          d.vitorias.quantidade_objetivo,
          d.oportunidades_perdidas.quantidade_objetivo
        ];

        const options = {
            series: [
                { name: 'Real', data: realData },
                { name: 'Objetivo', data: goalData }
            ],
            chart: { type: 'bar', height: 350, toolbar: { show: false } },
            plotOptions: {
                bar: { horizontal: false, columnWidth: getBarColumnWidth(categories.length), endingShape: 'rounded' },
            },
            dataLabels: { enabled: false },
            stroke: { show: true, width: 2, colors: ['transparent'] },
            xaxis: { categories: categories },
            colors: ['#2389D8', '#94A3B8'],
            fill: { opacity: 1 },
            tooltip: { shared: true, intersect: false }
        };
        const chart = new ApexCharts(disputasChartRef.current, options);
        let destruido = false;
        let renderizado = false;
        const stopObserving = renderChartWhenReady(disputasChartRef.current, chart, () => destruido, () => { renderizado = true; });
        return () => {
            destruido = true;
            stopObserving();
            // Só destrói se o render() realmente rodou - destruir um gráfico
            // que nunca chegou a desenhar não tem o que limpar e pode mexer
            // no DOM de forma inesperada.
            if (renderizado) {
                try { chart.destroy(); } catch (e) { console.warn('Erro ao destruir gráfico:', e); }
            }
        };
    }
  }, [disputasData]);

  // 3. Etapa do Funil
  useEffect(() => {
    if (etapaChartRef.current && etapaData?.data) {
        (etapaChartRef.current as HTMLElement).innerHTML = '';

        const d = etapaData.data;
        const categories = ['Novas Oportunidades', 'Pré-Disputa', 'Disputa', 'Monitoramento', 'Habilitação', 'Cotação Amanda', 'Cotação Mayara'];
        const realData = [
          d.novas_oportunidades.quantidade_real,
          d.pre_disputa.quantidade_real,
          d.disputa.quantidade_real,
          d.monitoramento.quantidade_real,
          d.habilitacao.quantidade_real,
          d.cotacao_amanda.quantidade_real,
          d.cotacao_mayara.quantidade_real
        ];
        const goalData = [
          d.novas_oportunidades.quantidade_objetivo,
          d.pre_disputa.quantidade_objetivo,
          d.disputa.quantidade_objetivo,
          d.monitoramento.quantidade_objetivo,
          d.habilitacao.quantidade_objetivo,
          d.cotacao_amanda.quantidade_objetivo,
          d.cotacao_mayara.quantidade_objetivo
        ];

        const options = {
            series: [
                { name: 'Real', data: realData },
                { name: 'Objetivo', data: goalData }
            ],
            chart: { type: 'bar', height: 350, toolbar: { show: false } },
            plotOptions: {
                bar: { horizontal: false, columnWidth: getBarColumnWidth(categories.length), endingShape: 'rounded' },
            },
            dataLabels: { enabled: false },
            stroke: { show: true, width: 2, colors: ['transparent'] },
            xaxis: { categories: categories },
            colors: ['#2389D8', '#94A3B8'],
            fill: { opacity: 1 },
            tooltip: { shared: true, intersect: false }
        };
        const chart = new ApexCharts(etapaChartRef.current, options);
        let destruido = false;
        let renderizado = false;
        const stopObserving = renderChartWhenReady(etapaChartRef.current, chart, () => destruido, () => { renderizado = true; });
        return () => {
            destruido = true;
            stopObserving();
            // Só destrói se o render() realmente rodou - destruir um gráfico
            // que nunca chegou a desenhar não tem o que limpar e pode mexer
            // no DOM de forma inesperada.
            if (renderizado) {
                try { chart.destroy(); } catch (e) { console.warn('Erro ao destruir gráfico:', e); }
            }
        };
    }
  }, [etapaData]);

  // 4. Perfil (pessoa selecionada) - Real por etapa, sem comparação com objetivo
  useEffect(() => {
    if (perfilChartRef.current && perfilData?.data) {
        (perfilChartRef.current as HTMLElement).innerHTML = '';

        const pessoa = perfilData.data.pessoas.find((p) => p.id_usuario === pessoaSelecionadaId);
        if (!pessoa) return;

        // Mostra só as etapas (dinâmicas, quaisquer ativas na conta) em que a
        // pessoa realmente tem oportunidade no período
        const etapasDisponiveis = perfilData.data.etapas ?? Object.keys(pessoa.por_etapa);
        const etapasComOportunidade = etapasDisponiveis.filter((nome) => (pessoa.por_etapa[nome] ?? 0) > 0);

        if (etapasComOportunidade.length === 0) return;

        const categories = etapasComOportunidade;
        const realData = etapasComOportunidade.map((nome) => pessoa.por_etapa[nome]);

        const options = {
            series: [
                { name: 'Oportunidades', data: realData }
            ],
            chart: { type: 'bar', height: 350, toolbar: { show: false } },
            plotOptions: {
                bar: { horizontal: false, columnWidth: getBarColumnWidth(categories.length), endingShape: 'rounded' },
            },
            title: {
                text: `${pessoa.nome_completo} — ${pessoa.quantidade_real} oportunidades (${formatCurrency(pessoa.valor_real ?? 0)})`,
                align: 'center' as const,
                style: { fontSize: '14px', fontWeight: 600 }
            },
            dataLabels: { enabled: true, offsetY: -20, style: { colors: ['#334155'] } },
            stroke: { show: true, width: 2, colors: ['transparent'] },
            xaxis: { categories: categories },
            colors: ['#2389D8'],
            fill: { opacity: 1 },
            tooltip: { shared: true, intersect: false }
        };
        const chart = new ApexCharts(perfilChartRef.current, options);
        let destruido = false;
        let renderizado = false;
        const stopObserving = renderChartWhenReady(perfilChartRef.current, chart, () => destruido, () => { renderizado = true; });
        return () => {
            destruido = true;
            stopObserving();
            // Só destrói se o render() realmente rodou - destruir um gráfico
            // que nunca chegou a desenhar não tem o que limpar e pode mexer
            // no DOM de forma inesperada.
            if (renderizado) {
                try { chart.destroy(); } catch (e) { console.warn('Erro ao destruir gráfico:', e); }
            }
        };
    }
  }, [perfilData, pessoaSelecionadaId]);

  const formatCurrency = (val: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

  // Intervalo do período de contratos, para os links dos cards "Novos"/"Renovações"
  // apontarem para a lista já filtrada pelo mesmo período do dashboard.
  // Prioriza o que o backend efetivamente usou; cai no cálculo local se ausente.
  const contratosPeriodoRange = (() => {
    const resp = contratosData?.data;
    if (resp?.periodo_data_inicio && resp?.periodo_data_fim) {
      return { dataInicio: resp.periodo_data_inicio, dataFim: resp.periodo_data_fim };
    }
    if (
      contratosFiltro.periodo === 'personalizado' &&
      contratosFiltro.customStart &&
      contratosFiltro.customEnd
    ) {
      return { dataInicio: contratosFiltro.customStart, dataFim: contratosFiltro.customEnd };
    }
    return getPeriodRange(contratosFiltro.periodo, contratosFiltro.referenceDate);
  })();

  const CONTRATOS_LISTA = '/crm/contratos-lista';
  const linkNovosContratos = `${CONTRATOS_LISTA}?filtro=novos&inicio=${contratosPeriodoRange.dataInicio}&fim=${contratosPeriodoRange.dataFim}`;
  const linkRenovacoesContratos = `${CONTRATOS_LISTA}?filtro=renovacoes&inicio=${contratosPeriodoRange.dataInicio}&fim=${contratosPeriodoRange.dataFim}`;

  // Helper for Loading Overlay
  const LoadingOverlay = () => (
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10
    }}>
      <div className="spinner-border text-primary" role="status" />
    </div>
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        
        {/* Page Header */}
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center ">
                <div className="col-md-4">
                  <h3 className="page-title">Dashboard Analytics</h3>
                </div>
                <div className="col-md-8 float-end ms-auto">
                  <div className="d-flex title-head">
                    <div className="daterange-picker d-flex align-items-center justify-content-center">
                      <div className="form-sort me-2">
                        
                      </div>
                      <div className="head-icons mb-0">
                        <CollapseHeader/>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ROW 1: Faturamento (Real vs Objetivo) */}
        <div className="row">
          <div className="col-md-12">
            <div className="card flex-fill position-relative"> 
              <div className="card-header border-0 pb-0">
                 <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                    <h4 className="dash-section-title"><i className="ti ti-chart-area-line" /> Faturamento (Real vs Objetivo)</h4>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={handleExportarFaturamentoPDF}
                        disabled={exportandoFaturamentoPDF || loadingFaturamento}
                      >
                        {exportandoFaturamentoPDF ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Gerando PDF...
                          </>
                        ) : (
                          <>
                            <i className="ti ti-file-type-pdf me-2"></i>
                            Exportar PDF
                          </>
                        )}
                      </button>
                      <DashboardPeriodFilter value={faturamentoFiltro} onChange={setFaturamentoFiltro} />
                    </div>
                 </div>
              </div>
              <div className="card-body position-relative">
                 {loadingFaturamento && <LoadingOverlay />}
                 {faturamentoData ? (
                     <>
                        <div className="stat-grid mb-4">
                            <StatCard
                                color="blue"
                                icon="ti-cash"
                                label="Real"
                                value={formatCurrency(faturamentoData.data.faturamento.valor_real)}
                            />
                            <StatCard
                                color="slate"
                                icon="ti-target-arrow"
                                label="Objetivo"
                                value={formatCurrency(faturamentoData.data.faturamento.valor_objetivo)}
                            />
                            <StatCard
                                color={(faturamentoData.data.faturamento.percentual_alcance ?? 0) >= 100 ? 'green' : 'amber'}
                                icon="ti-percentage"
                                label="Alcance"
                                value={`${faturamentoData.data.faturamento.percentual_alcance}%`}
                            />
                            <StatCard
                                color="blue"
                                icon="ti-file-invoice"
                                label="Notas Emitidas"
                                value={faturamentoData.data.detalhes.total_receitas}
                                sub="NFs de venda no período"
                            />
                        </div>
                        <div id="faturamento-chart" ref={faturamentoChartRef} />
                     </>
                 ) : (!loadingFaturamento && <div className="text-center p-5">Nenhum dado disponível.</div>)}
              </div>
            </div>
          </div>
        </div>

        {/* ROW 2: Operacional (Status, Etapa do Funil, Perfil) */}
        <div className="row">
          <div className="col-md-12">
             <div className="card flex-fill position-relative">
                <div className="card-header border-0 pb-0">
                    <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2 mb-2">
                        <h4 className="dash-section-title mb-0"><i className="ti ti-chart-bar" /> Operacional: Real vs Objetivo</h4>
                        {/* Exporta o PDF da aba ativa (uma tabela por card da aba) */}
                        {operacionalTab === 'status' && renderBotaoExportarQuadrante('status', disputasFiltro, 'Relatorio_Oportunidades_Status', loadingDisputas)}
                        {operacionalTab === 'etapa' && renderBotaoExportarQuadrante('etapa', etapaFiltro, 'Relatorio_Oportunidades_Etapa_Funil', loadingEtapa)}
                        {operacionalTab === 'perfil' && renderBotaoExportarQuadrante('perfil', perfilFiltro, 'Relatorio_Oportunidades_Perfil', loadingPerfil)}
                    </div>
                    <ul className="nav nav-tabs nav-tabs-bordered">
                        <li className="nav-item">
                            <button type="button" className={`nav-link ${operacionalTab === 'status' ? 'active' : ''}`} onClick={() => setOperacionalTab('status')}>
                                <i className="ti ti-list-check me-1" /> Status
                            </button>
                        </li>
                        <li className="nav-item">
                            <button type="button" className={`nav-link ${operacionalTab === 'etapa' ? 'active' : ''}`} onClick={() => setOperacionalTab('etapa')}>
                                <i className="ti ti-git-branch me-1" /> Etapa do Funil
                            </button>
                        </li>
                        <li className="nav-item">
                            <button type="button" className={`nav-link ${operacionalTab === 'perfil' ? 'active' : ''}`} onClick={() => setOperacionalTab('perfil')}>
                                <i className="ti ti-user me-1" /> Perfil
                            </button>
                        </li>
                    </ul>
                </div>
                <div className="card-body position-relative">
                    <div className="tab-content">
                        {/* STATUS: só os 5 status literais de oportunidades.status */}
                        {/* DIAGNÓSTICO TEMPORÁRIO: IIFE com try/catch pra capturar e mostrar
                            qualquer erro de render desse bloco em vez de sumir silenciosamente
                            (não existe Error Boundary no app - sem isso um throw aqui não
                            deixaria rastro nenhum). Reverter pro JSX puro depois de achar a causa. */}
                        {(() => {
                          try {
                            console.log(`%c[DEBUG STATUS] instância ${instanceIdRef.current} render - operacionalTab=`, 'color:orange;font-weight:bold', operacionalTab, 'loadingDisputas=', loadingDisputas, 'disputasData=', disputasData, 'attached=', document.body.contains(disputasChartRef.current));
                            return (
                              <div ref={debugStatusPaneRef} className={`tab-pane fade ${operacionalTab === 'status' ? 'show active' : ''}`}>
                                  <div className="d-flex align-items-center justify-content-end mb-3">
                                      <DashboardPeriodFilter value={disputasFiltro} onChange={setDisputasFiltro} />
                                  </div>
                                  {loadingDisputas && <LoadingOverlay />}
                                  {disputasData?.data ? (
                                      <>
                                          <div className="stat-grid mb-4">
                                              <StatCard
                                                  color="slate"
                                                  icon="ti-clock"
                                                  label="Em Andamento"
                                                  value={`${disputasData.data.oportunidades_andamento.quantidade_real} / ${disputasData.data.oportunidades_andamento.quantidade_objetivo}`}
                                                  sub={formatCurrency(disputasData.data.oportunidades_andamento.valor_real ?? 0)}
                                              />
                                              <StatCard
                                                  color="slate"
                                                  icon="ti-player-pause"
                                                  label="Suspenso"
                                                  value={`${disputasData.data.oportunidades_suspensas.quantidade_real} / ${disputasData.data.oportunidades_suspensas.quantidade_objetivo}`}
                                                  sub={formatCurrency(disputasData.data.oportunidades_suspensas.valor_real ?? 0)}
                                              />
                                              <StatCard
                                                  color="slate"
                                                  icon="ti-ban"
                                                  label="Cancelada"
                                                  value={`${disputasData.data.oportunidades_canceladas.quantidade_real} / ${disputasData.data.oportunidades_canceladas.quantidade_objetivo}`}
                                                  sub={formatCurrency(disputasData.data.oportunidades_canceladas.valor_real ?? 0)}
                                              />
                                              <StatCard
                                                  color="green"
                                                  icon="ti-trophy"
                                                  label="Ganha"
                                                  value={`${disputasData.data.vitorias.quantidade_real} / ${disputasData.data.vitorias.quantidade_objetivo}`}
                                                  sub={formatCurrency(disputasData.data.vitorias.valor_real ?? 0)}
                                              />
                                              <StatCard
                                                  color="red"
                                                  icon="ti-x"
                                                  label="Perdida"
                                                  value={`${disputasData.data.oportunidades_perdidas.quantidade_real} / ${disputasData.data.oportunidades_perdidas.quantidade_objetivo}`}
                                                  sub={formatCurrency(disputasData.data.oportunidades_perdidas.valor_real ?? 0)}
                                              />
                                          </div>
                                          <div id="disputas-chart" ref={disputasChartRef} />
                                      </>
                                  ) : (!loadingDisputas && <div className="text-center p-5">Nenhum dado disponível.</div>)}
                              </div>
                            );
                          } catch (err) {
                            console.error('%c[DEBUG STATUS] ERRO AO RENDERIZAR:', 'color:red;font-weight:bold', err);
                            return (
                              <div className={`tab-pane fade ${operacionalTab === 'status' ? 'show active' : ''}`} style={{ color: 'red', padding: 20, border: '2px solid red' }}>
                                ERRO DE RENDER NO STATUS: {String(err)}
                              </div>
                            );
                          }
                        })()}

                        {/* ETAPA DO FUNIL */}
                        <div className={`tab-pane fade ${operacionalTab === 'etapa' ? 'show active' : ''}`}>
                            <div className="d-flex align-items-center justify-content-end mb-3">
                                <DashboardPeriodFilter value={etapaFiltro} onChange={setEtapaFiltro} />
                            </div>
                            {loadingEtapa && <LoadingOverlay />}
                            {etapaData?.data ? (
                                <div className="stat-grid">
                                    <StatCard
                                        color="green"
                                        icon="ti-circle-plus"
                                        label="Novas Oportunidades"
                                        value={`${etapaData.data.novas_oportunidades.quantidade_real} / ${etapaData.data.novas_oportunidades.quantidade_objetivo}`}
                                        sub={formatCurrency(etapaData.data.novas_oportunidades.valor_real ?? 0)}
                                    />
                                    <StatCard
                                        color="amber"
                                        icon="ti-clock-play"
                                        label="Pré-Disputa"
                                        value={`${etapaData.data.pre_disputa.quantidade_real} / ${etapaData.data.pre_disputa.quantidade_objetivo}`}
                                        sub={formatCurrency(etapaData.data.pre_disputa.valor_real ?? 0)}
                                    />
                                    <StatCard
                                        color="amber"
                                        icon="ti-gavel"
                                        label="Disputa"
                                        value={`${etapaData.data.disputa.quantidade_real} / ${etapaData.data.disputa.quantidade_objetivo}`}
                                        sub={formatCurrency(etapaData.data.disputa.valor_real ?? 0)}
                                    />
                                    <StatCard
                                        color="slate"
                                        icon="ti-eye"
                                        label="Monitoramento"
                                        value={`${etapaData.data.monitoramento.quantidade_real} / ${etapaData.data.monitoramento.quantidade_objetivo}`}
                                        sub={formatCurrency(etapaData.data.monitoramento.valor_real ?? 0)}
                                    />
                                    <StatCard
                                        color="blue"
                                        icon="ti-certificate"
                                        label="Habilitação"
                                        value={`${etapaData.data.habilitacao.quantidade_real} / ${etapaData.data.habilitacao.quantidade_objetivo}`}
                                        sub={formatCurrency(etapaData.data.habilitacao.valor_real ?? 0)}
                                    />
                                    <StatCard
                                        color="blue"
                                        icon="ti-file-dollar"
                                        label="Cotação Amanda"
                                        value={etapaData.data.cotacao_amanda.quantidade_real}
                                        sub={formatCurrency(etapaData.data.cotacao_amanda.valor_real ?? 0)}
                                    />
                                    <StatCard
                                        color="blue"
                                        icon="ti-file-dollar"
                                        label="Cotação Mayara"
                                        value={etapaData.data.cotacao_mayara.quantidade_real}
                                        sub={formatCurrency(etapaData.data.cotacao_mayara.valor_real ?? 0)}
                                    />
                                </div>
                            ) : (!loadingEtapa && <div className="text-center p-5">Nenhum dado disponível.</div>)}
                            <div id="etapa-chart" ref={etapaChartRef} />
                        </div>

                        {/* PERFIL */}
                        <div className={`tab-pane fade ${operacionalTab === 'perfil' ? 'show active' : ''}`}>
                            <div className="d-flex align-items-center justify-content-end mb-3">
                                <DashboardPeriodFilter value={perfilFiltro} onChange={setPerfilFiltro} />
                            </div>
                            {loadingPerfil && <LoadingOverlay />}
                            {perfilData?.data ? (
                                <>
                                    <div className="mb-3" style={{ maxWidth: 320 }}>
                                        <label className="form-label">Pessoa</label>
                                        <select
                                            className="form-select form-select-sm"
                                            value={pessoaSelecionadaId ?? ''}
                                            onChange={(e) => setPessoaSelecionadaId(Number(e.target.value))}
                                        >
                                            {perfilData.data.pessoas.length === 0 && (
                                                <option value="">Nenhuma pessoa com oportunidades no período</option>
                                            )}
                                            {perfilData.data.pessoas.map((p) => (
                                                <option key={p.id_usuario} value={p.id_usuario}>{p.nome_completo}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {(() => {
                                        const pessoa = perfilData.data.pessoas.find((p) => p.id_usuario === pessoaSelecionadaId);
                                        const etapasDisponiveis = perfilData.data.etapas ?? (pessoa ? Object.keys(pessoa.por_etapa) : []);
                                        const etapasComOportunidade = pessoa
                                            ? etapasDisponiveis.filter((nome) => (pessoa.por_etapa[nome] ?? 0) > 0)
                                            : [];
                                        // Todas as informações (total, valor, detalhe por etapa) ficam
                                        // dentro do próprio gráfico (título + rótulos nas barras).
                                        if (pessoa && etapasComOportunidade.length === 0) {
                                            return <div className="text-center p-4 text-muted small">Nenhuma oportunidade em etapa específica no período.</div>;
                                        }
                                        return <div id="perfil-chart" ref={perfilChartRef} />;
                                    })()}
                                </>
                            ) : (!loadingPerfil && <div className="text-center p-5">Nenhum dado disponível.</div>)}
                        </div>
                    </div>
                </div>
             </div>
          </div>
        </div>

        {/* ROW 3: Contratos (Financeiro) */}
        <div className="row mt-4">
           <div className="col-md-12">
              <div className="card flex-fill position-relative">
                 <div className="card-header border-0 pb-0">
                     <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                        <h4 className="dash-section-title"><i className="ti ti-file-invoice" /> Indicadores de Contratos</h4>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          {renderBotaoExportarQuadrante('contratos', contratosFiltro, 'Relatorio_Indicadores_Contratos', loadingContratos)}
                          <DashboardPeriodFilter value={contratosFiltro} onChange={setContratosFiltro} />
                        </div>
                     </div>
                     <p className="text-muted small mb-0 mt-1">Contratos Ativos e Vencendo em 3 Meses mostram sempre a situação atual, sem filtro de período.</p>
                 </div>
                 <div className="card-body position-relative">
                    {loadingContratos && <LoadingOverlay />}
                    <div className="stat-grid">
                        <StatCard
                            color="blue"
                            icon="ti-file-check"
                            label="Contratos Ativos"
                            value={contratosData?.data.contrato_ativo_total.quantidade_contratos ?? 0}
                            sub={formatCurrency(contratosData?.data.contrato_ativo_total.valor_real ?? 0)}
                            to={CONTRATOS_LISTA}
                        />
                        <StatCard
                            color="amber"
                            icon="ti-clock-exclamation"
                            label="Vencendo em 3 Meses"
                            value={contratosData?.data.contratos_a_vencer.quantidade_contratos ?? 0}
                            sub={formatCurrency(contratosData?.data.contratos_a_vencer.valor_real ?? 0)}
                            to={`${CONTRATOS_LISTA}?filtro=a-vencer`}
                        />
                        <StatCard
                            color="green"
                            icon="ti-circle-plus"
                            label="Novos"
                            value={contratosData?.data.novos_contratos.quantidade_contratos ?? 0}
                            sub={formatCurrency(contratosData?.data.novos_contratos.valor_real ?? 0)}
                            to={linkNovosContratos}
                        />
                        <StatCard
                            color="slate"
                            icon="ti-refresh"
                            label="Renovações"
                            value={contratosData?.data.renovacoes_contrato.quantidade_renovacoes ?? 0}
                            sub={formatCurrency(contratosData?.data.renovacoes_contrato.valor_real ?? 0)}
                            to={linkRenovacoesContratos}
                        />
                        <StatCard
                            color="green"
                            icon="ti-cash-banknote"
                            label="Saldo Disponível (Não Empenhado)"
                            value={formatCurrency(contratosData?.data.saldo_disponivel_contratos?.valor_real ?? 0)}
                            sub={`${contratosData?.data.saldo_disponivel_contratos?.quantidade_contratos ?? 0} contratos ativos`}
                            to={CONTRATOS_LISTA}
                        />
                    </div>
                 </div>
              </div>
           </div>
        </div>

        {/* ROW 4: Fluxo de Caixa */}
        <div className="row mt-4">
           <div className="col-md-12">
                <div className="card flex-fill position-relative">
                    <div className="card-header border-0 pb-0">
                        <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                            <h4 className="dash-section-title"><i className="ti ti-cash" /> Fluxo de Caixa</h4>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              {renderBotaoExportarQuadrante('fluxo-caixa', fluxoCaixaFiltro, 'Relatorio_Fluxo_Caixa', loadingFluxoCaixa)}
                              <DashboardPeriodFilter value={fluxoCaixaFiltro} onChange={setFluxoCaixaFiltro} />
                            </div>
                        </div>
                    </div>
                    <div className="card-body position-relative">
                        {loadingFluxoCaixa && <LoadingOverlay />}
                        <div className="stat-grid">
                            <StatCard
                                color="blue"
                                icon="ti-wallet"
                                label="Saldo Contas Correntes"
                                value={
                                    fluxoCaixaData?.data.saldo_contas_correntes.sem_lancamento_periodo
                                        ? "Sem lançamento"
                                        : formatCurrency(fluxoCaixaData?.data.saldo_contas_correntes.valor_real ?? 0)
                                }
                                sub={`${fluxoCaixaData?.data.saldo_contas_correntes.quantidade_contas ?? 0} contas`}
                            />
                            <StatCard
                                color="red"
                                icon="ti-arrow-up-right"
                                label="Contas a Pagar"
                                value={formatCurrency(fluxoCaixaData?.data.contas_a_pagar.valor_real ?? 0)}
                                sub={`${fluxoCaixaData?.data.contas_a_pagar.quantidade_titulos ?? 0} títulos`}
                            />
                            <StatCard
                                color="green"
                                icon="ti-arrow-down-left"
                                label="Contas a Receber"
                                value={formatCurrency(fluxoCaixaData?.data.contas_a_receber.valor_real ?? 0)}
                                sub={`${fluxoCaixaData?.data.contas_a_receber.quantidade_titulos ?? 0} títulos`}
                            />
                        </div>
                    </div>
                </div>
           </div>
        </div>

      </div>
    </div>
  );
};

export default DashboardAnalytics;